<template>

<svg 
    :width="chart.config.width"
    :height="chart.config.height"
    :class="{'is-tuto': tuto}"
>

    <!-- Shadow filter -->
    <defs>
        <filter id="f2" x="0" y="0" width="200%" height="200%">
            <feOffset result="offOut" in="SourceAlpha" dx="2" dy="2" />
            <feGaussianBlur in="SourceAlpha" stdDeviation="5"/> 
            <feComponentTransfer>
                <feFuncA type="linear" slope="0.2"/>
            </feComponentTransfer>
            <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
        </filter>
    </defs>
    
    <rect class="bg" :width="chart.config.width" :height="chart.config.height" fill="white" @mouseenter="onGroupMouseleave()"></rect>

    <g :transform="`translate(${chart.config.width/2},${chart.config.height/2})`" @mouseleave="onGroupMouseleave()">

        <circle class="bg" :r="chart.config.height/2 - 20" fill="white"></circle>

        <g class="arcs">
            <g v-for="group in chart.sync.groups" :key="group.index" class="arc" @mouseover="onGroupMouseover(group)">

                <title>{{ group.name }} : {{ group.value }} adaptations</title>

                <path
                    class="arc"
                    :fill="chart.color(group.index)"
                    :d="chart.arc(group)"
                ></path>

                <path
                    :id="`arc${group.index}-${uuid}`"
                    :ref="`arc${group.index}-${uuid}`"
                    fill="white"
                    :d="chart.thinArc(group)"
                ></path>

                <text
                    v-if="(group.endAngle - group.startAngle) * 45 > group.name.length"
                    class="arc-text"
                    dy="-12"
                >
                    <textPath
                        :xlink:href="`#arc${group.index}-${uuid}`"
                        startOffset="25%"
                        text-anchor="middle"
                        font-size="14px"
                        font-weight="700"
                        :fill="chart.color(group.index)"
                    >{{ group.name }}</textPath>
                </text>

            </g>
        </g>

        <g class="chords">
            <g
                v-for="(chord, i) in Object.values(chart.sync.chords)"
                :key="chord.index"
                class="chord-wrapper"
                :class="{'is-tuto': i == 2}"
            >
                <template v-if="chord.source.value > 0">

                    <title>{{ chart.sync.groups[chord.source.index].name }} -> {{ chart.sync.groups[chord.target.index].name }} : {{ chord.source.value }} adaptations
                    </title>
                    <path
                        class="chord"
                        @click="onChordClick(chord)"
                        :d="chart.ribbon.radius(side => ribbonAngle(chord, side))(chord)"
                        :fill="chart.color(chord.source.index)"
                        :class="{'is-visible': chord.visible}"
                    ></path>
                </template>
            </g>
        </g>

        <g class="arrows">
            <g
                v-for="(chord, i) in Object.values(chart.sync.chords)"
                :key="chord.index"
                class="arrow"
                :class="{'is-tuto': i == 2}"
            >
                <template v-if="chord.source.value > 0">
                    <polygon
                        v-if="chord.target.endAngle - chord.target.startAngle > 0.045"
                        class="arrow"
                        points="0,0 14,0 7,16"
                        fill="#000"
                        style="pointer-events: none"
                        :transform="`rotate(${181.2 + (360 / Math.PI / 2) * (chord.target.startAngle + (chord.target.endAngle - chord.target.startAngle)/2)}) translate(0,${310})`"
                        :class="{'is-visible': chord.visible}"
                    ></polygon>
                </template>
                <template v-if="chord.source.value > 0">
                    <polygon
                        v-if="chord.source.endAngle - chord.source.startAngle > 0.045"
                        class="arrow"
                        points="0,16 14,16 7,0"
                        fill="#000"
                        style="pointer-events: none"
                        :transform="`rotate(${181.2 + (360 / Math.PI / 2) * (chord.source.startAngle + (chord.source.endAngle - chord.source.startAngle)/2)}) translate(0,${320})`"
                        :class="{'is-visible': chord.visible}"
                    ></polygon>
                </template>
            </g>
        </g>
    </g>

    <g v-if="tuto && chart != null" class="tuto" transform="translate(35,35)">
        <foreignObject width="420" height="200">
            <div class="box has-text-left">
                <span class="heading">Tutoriel</span>
                <p class="mb-10">
                    Ce flux représente <strong>{{ tutoFlux.source.value }}</strong> adaptations qui proviennent des 
                    <strong :style="{color: chart.color(tutoFlux.source.index)}">{{ groups[tutoFlux.source.index].name }}</strong> et qui ont été adaptées au 
                    <strong :style="{color: chart.color(tutoFlux.target.index)}">{{ groups[tutoFlux.target.index].name }}</strong>
                </p>
                <button @click="$emit('endTuto')" class="button is-dark is-small">Ok, j'ai compris</button>
            </div>
        </foreignObject>
        <path transform="translate(435,75)" fill="#fff" class="st0" d="M50.4,63.8C50.4,63.8,50.4,63.9,50.4,63.8c-0.3,0.1-0.5,0.2-0.8,0.2c0,0-0.1,0-0.1,0c-0.1,0-0.2,0-0.2,0
				c-0.2,0-0.4,0-0.6-0.1c-0.1,0-0.2,0-0.2-0.1c-0.3-0.1-0.5-0.2-0.7-0.4c0,0,0,0,0,0c-0.2-0.1-0.3-0.3-0.4-0.5l-8-13.5
				c-0.6-1-0.1-2.2,1-2.8c1.2-0.5,2.6-0.1,3.2,0.9l5.1,8.6c1.2-7.8-0.6-16-0.6-16.1C44,17.5,23.7,1.6,2.7,4.4C1.4,4.6,0.2,3.8,0,2.7
				c0-0.1,0-0.2,0-0.3c0-1,0.8-1.9,2-2c23.5-3.2,46.3,14.2,50.7,38.9c0.1,0.4,1.8,8.4,0.7,16.5l6.6-5.6c0.9-0.8,2.4-0.8,3.3,0
				c0.9,0.8,0.9,2.1,0,2.9L51.1,63.4c0,0-0.1,0-0.1,0.1C50.9,63.6,50.7,63.8,50.4,63.8z"/>
    </g>
</svg>
</template>

<script>
import { rgb } from 'd3'
import ChordService from './chord.js'
import { uuid } from '@/services/helpers'

export default {
    props: {
        matrix: {
            required: true,
            type: Array
        },
        groups: {
            required: true,
            type: Array
        },
        highlight: {
            required: false
        },
        tuto: {
            default: false,
            type: Boolean
        }
    },
    data () {
        return {
            chart: null,
            uuid: null // to create different ids when mulitple chord charts are created on the same page
        }
    },
    created () {
        this.uuid = uuid()
        this.chart = new ChordService(this.matrix, this.groups)
    },
    methods: {
        darker (color) {
            return rgb(color).darker()
        },
        ribbonColor (chord) {
            return chord.source.index
        },
        ribbonAngle (data, side) {
            if (
                data.source.index != side.index // When the ribbon goes to another section
                || (data.source.index == data.target.index && data.source.endAngle < side.startAngle) // When the ribbon returns on itself
            ) {
                return this.chart.config.innerRadius - 15
            }
            return this.chart.config.innerRadius - 3
        },
        onGroupMouseover (group) {
            Object.values(this.chart.sync.chords).forEach(chord => {
                chord.visible = (chord.source.index == group.index || chord.target.index == group.index)
            })
        },
        onGroupMouseleave () {
            Object.values(this.chart.sync.chords).forEach(chord => {
                chord.visible = true
            })
        },
        onChordClick (chord) {
            this.$emit('chordClick', chord)
        }
    },
    computed: {
        tutoFlux () {
            return Object.values(this.chart.sync.chords)[2]
        }
    },
    watch: {
        matrix: function() {
            this.$nextTick(() => {
                this.chart.update(this.matrix, this.groups)
            })
        },
        highlight: function(data) {
            Object.values(this.chart.sync.chords).forEach(chord => {
                chord.visible = data == null || (data != null && chord.source.index == data[0] && chord.target.index == data[1])
            })
        }
    }
}
</script>

<style lang="scss">

.chord {
  transition: opacity 0.2s;
}

path.chord {
  opacity: 0.05;

  &.is-visible {
    opacity: 0.8;
  }
}

polygon.arrow {
  opacity: 0.0;

  &.is-visible {
    opacity: 0.15;
  }
}

@keyframes fade {
    0% {
        opacity: 0;
    }
    50% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

svg.is-tuto {
    .chord-wrapper:not(.is-tuto) {
        opacity: 0.05;
    }
    .chord-wrapper.is-tuto {
        animation: fade 0.6s ease-in-out forwards;;
    }
    .tuto {
        animation: fade 0.9s ease-in-out;
    }
    .arrow:not(.is-tuto) {
        opacity: 0.15;
    }
}

</style>